/**
 * MODULE: domain/pages/frontend/components/CommentMentionInput.tsx
 * PURPOSE: Comment compose textarea with workspace member @mention autocomplete
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import type { WorkspaceMember } from '@domain/workspace';
import { Textarea } from 'flowbite-react';
import {
  buildMentionTokenForMember,
  filterMembersForMentionQuery,
  getActiveMentionQuery,
  insertMentionAtQuery,
} from '../utils/commentMentionUtils';
import { formatWorkspaceMemberLabel } from '../utils/pageShareMemberUtils';

type Props = {
  value: string;
  onChange: (value: string) => void;
  members: WorkspaceMember[];
  currentUserId?: string;
  placeholder?: string;
  rows?: number;
  disabled?: boolean;
  onSubmit?: () => void;
  submitDisabled?: boolean;
};

export function CommentMentionInput({
  value,
  onChange,
  members,
  currentUserId,
  placeholder = 'Add a comment…',
  rows = 3,
  disabled = false,
  onSubmit,
  submitDisabled = false,
}: Props) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [caretIndex, setCaretIndex] = useState(0);
  const [activeIndex, setActiveIndex] = useState(0);

  const mentionQuery = useMemo(() => {
    const atCaret = getActiveMentionQuery(value, caretIndex);
    if (atCaret) {
      return atCaret;
    }
    // Playwright fill() and some paste paths leave caret state stale — check end of text.
    return getActiveMentionQuery(value, value.length);
  }, [value, caretIndex]);

  const effectiveCaretIndex = mentionQuery?.atIndex ?? caretIndex;

  const mentionOptions = useMemo(
    () =>
      mentionQuery
        ? filterMembersForMentionQuery(members, mentionQuery.query, {
            excludeUserId: currentUserId,
          })
        : [],
    [members, mentionQuery, currentUserId]
  );

  const showMentionMenu = Boolean(mentionQuery && mentionOptions.length > 0);

  const syncCaret = useCallback(() => {
    const element = textareaRef.current;
    if (!element) {
      return;
    }
    setCaretIndex(element.selectionStart ?? value.length);
  }, [value.length]);

  const insertMention = useCallback(
    (member: WorkspaceMember) => {
      const token = buildMentionTokenForMember(member);
      const { nextText, nextCaret } = insertMentionAtQuery(
        value,
        effectiveCaretIndex === caretIndex ? caretIndex : value.length,
        token
      );
      onChange(nextText);
      setActiveIndex(0);
      requestAnimationFrame(() => {
        const element = textareaRef.current;
        if (!element) {
          return;
        }
        element.focus();
        element.setSelectionRange(nextCaret, nextCaret);
        setCaretIndex(nextCaret);
      });
    },
    [caretIndex, effectiveCaretIndex, onChange, value]
  );

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (
      onSubmit &&
      (event.metaKey || event.ctrlKey) &&
      event.key === 'Enter' &&
      !submitDisabled
    ) {
      event.preventDefault();
      onSubmit();
      return;
    }
    if (!showMentionMenu) {
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((index) => (index + 1) % mentionOptions.length);
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex(
        (index) => (index - 1 + mentionOptions.length) % mentionOptions.length
      );
      return;
    }
    if (event.key === 'Enter' || event.key === 'Tab') {
      event.preventDefault();
      const selected = mentionOptions[activeIndex];
      if (selected) {
        insertMention(selected);
      }
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      onChange(value.slice(0, mentionQuery!.atIndex) + value.slice(caretIndex));
    }
  };

  return (
    <div className="relative">
      <Textarea
        ref={textareaRef}
        rows={rows}
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        data-testid="pages-comment-input"
        onChange={(event) => {
          onChange(event.target.value);
          setCaretIndex(event.target.selectionStart ?? event.target.value.length);
          setActiveIndex(0);
        }}
        onClick={syncCaret}
        onKeyUp={syncCaret}
        onKeyDown={handleKeyDown}
      />
      {showMentionMenu && (
        <ul
          className="absolute z-20 mt-1 max-h-48 w-full overflow-y-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-800"
          role="listbox"
          data-testid="pages-comment-mention-menu"
        >
          {mentionOptions.map((member, index) => (
            <li key={member.user_id} role="option" aria-selected={index === activeIndex}>
              <button
                type="button"
                className={`flex w-full px-3 py-2 text-left text-sm ${
                  index === activeIndex
                    ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-200'
                    : 'text-gray-800 hover:bg-gray-50 dark:text-gray-100 dark:hover:bg-gray-700'
                }`}
                data-testid={`pages-comment-mention-option-${member.user_id}`}
                onMouseDown={(event) => {
                  event.preventDefault();
                  insertMention(member);
                }}
              >
                {formatWorkspaceMemberLabel(member)}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
